/**
 * The teams a workbench or node reported it holds (`device.teams`), in the Teams section's words: the team's name (a
 * link to it in Teams when the organisation has it), "team" or "specialist", where it is kept, and its members. No
 * versions or ids. A machine's page and the Nodes page both show them this way.
 */

import type { DeviceTeam } from '@aioe/shared';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { routes } from '../../api/client.ts';
import { useLoad } from '../../hooks.ts';
import type { ArtTone } from '../ui/Art.tsx';
import { ListBlock, ListGroup, ListRow } from '../ui.tsx';

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** "Ana, Ben · 2 agents", as the Teams section says a team's members. */
export function deviceMemberWords(members?: DeviceTeam['members']): string {
  if (!members?.length) return '';
  const agents = members.filter((m) => m.kind !== 'person').length;
  return `${members.map((m) => m.name).join(', ')}${agents ? ` · ${plural(agents, 'agent', 'agents')}` : ''}`;
}

/** The names of the projects a machine's teams are kept in, read once when one is. */
export function useTeamProjectNames(teams: DeviceTeam[] | undefined): (id?: string) => string | undefined {
  const need = !!teams?.some((t) => t.scope === 'project');
  const projects = useLoad(() => (need ? routes.projects().catch(() => []) : Promise.resolve([])), [need]);
  return (id) => (id ? projects.data?.find((p) => p.id === id)?.name : undefined);
}

/** One team as a ListRow of a ListGroup: its name (a link in Teams), what it is and where it is kept, its members; `status` and `control` at the end. */
export function DeviceTeamRow({ t, projectName, status, control }: { t: DeviceTeam; projectName: (id?: string) => string | undefined; status?: ReactNode; control?: ReactNode }) {
  const where = t.scope === 'organisation' ? 'The organisation’s, every project' : t.scope === 'project' ? `In ${projectName(t.projectId) ?? 'a project'}` : t.kept ? 'Kept on this machine by choice' : 'Not shared yet';
  const members = deviceMemberWords(t.members);
  return (
    <ListRow
      title={
        t.scope === 'local' ? (
          t.name
        ) : (
          <Link to={`/teams/${encodeURIComponent(t.id)}`} className="text-accent hover:underline">
            {t.name}
          </Link>
        )
      }
      description={
        <>
          {`${t.kind === 'specialist' ? 'Specialist' : 'Team'} · ${where}`}
          {members ? <span className="block">{members}</span> : null}
        </>
      }
      status={status}
      control={control}
      compactControl={Boolean(control)}
    />
  );
}

export function TeamsOnMachine({ tone: _tone, teams, className }: { tone: ArtTone; teams?: DeviceTeam[]; className?: string }) {
  const projectName = useTeamProjectNames(teams);
  const rows = [...(teams ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  return (
    <ListGroup title="Teams" count={rows.length || undefined} description="The teams and specialists this machine holds, as it last reported them." className={className}>
      {rows.length ? (
        rows.map((t) => <DeviceTeamRow key={`${t.scope}:${t.id}`} t={t} projectName={projectName} />)
      ) : (
        <ListBlock className="text-sm text-muted">{teams ? 'No teams on this machine.' : 'This machine has not reported the teams it holds yet.'}</ListBlock>
      )}
    </ListGroup>
  );
}
